import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isAdminRole, VISIBILITY_LABEL } from "../lib/access";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { PriorityBadge, TaskStatusBadge, HandoffStatusBadge, OverdueBadge } from "../components/PriorityBadge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import {
  ArrowLeft, CheckCircle2, Wrench, AlertTriangle, MessageSquare,
  Check, ArrowRightCircle, ClipboardList, UserCheck, CheckSquare, Archive, Pencil,
} from "lucide-react";
import { toast } from "sonner";


function sendToLabel(h) {
  const rt = h.recipient_target;
  if (rt === "specific_shift") return `${h.division || ""} · ${h.next_shift || "shift"}`.trim();
  if (rt === "all_shifts") return `All shifts in ${h.division || "division"}`;
  if (rt === "selected_people") {
    const n = (h.recipient_user_ids || []).length || (h.intended_recipients || []).length;
    return `${n} selected ${n === 1 ? "person" : "people"}`;
  }
  // Legacy handoffs created before recipient targeting
  const parts = [VISIBILITY_LABEL[h.visibility] || h.visibility];
  if (h.next_shift) parts.push(h.next_shift);
  return parts.filter(Boolean).join(" · ");
}

function Section({ icon: Icon, title, children, tint = "text-slate-500" }) {
  return (
    <div>
      <h3 className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${tint}`}>
        <Icon className="h-4 w-4" /> {title}
      </h3>
      <div className="mt-2 text-sm leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

export default function HandoffDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        timeZone: user?.org_timezone || "America/Chicago",
        weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };
  const [h, setH] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(() => {
    api.get(`/handoffs/${id}`)
      .then((r) => { setH(r.data); setLoadError(null); })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403) setLoadError(err.response?.data?.detail || "You do not have permission to view this record");
        else setLoadError("This handoff could not be found.");
      });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    await api.post(`/handoffs/${id}/acknowledge`);
    toast.success("Handoff acknowledged");
    load();
  };

  const completeHandoff = async () => {
    await api.post(`/handoffs/${id}/complete`);
    toast.success("Handoff marked completed");
    load();
  };

  const archiveHandoff = async () => {
    await api.post(`/handoffs/${id}/archive`);
    toast.success("Handoff archived");
    load();
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/handoffs/${id}/comments`, { text: comment.trim() });
      setComment("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const taskAction = async (taskId, action) => {
    await api.patch(`/tasks/${taskId}/${action}`);
    toast.success(action === "complete" ? "Task completed" : "Task carried forward");
    load();
  };

  if (loadError) {
    return (
      <div className="mx-auto max-w-2xl animate-fade-up">
        <button onClick={() => navigate("/dashboard")} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <Card data-testid="handoff-access-denied" className="rounded-md border border-slate-200 bg-white p-10 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
          <h2 className="mt-3 font-display text-xl font-bold text-slate-900">{loadError}</h2>
          <p className="mt-1 text-sm text-slate-500">If you believe this is a mistake, contact your organization admin.</p>
        </Card>
      </div>
    );
  }

  if (!h) return <div className="h-64 animate-pulse rounded-md bg-slate-200/70" />;

  return (
    <div className="mx-auto max-w-4xl animate-fade-up">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display text-3xl font-black tracking-tight text-slate-900" data-testid="handoff-detail-title">{h.title}</h1>
            <PriorityBadge priority={h.priority} />
            <HandoffStatusBadge status={h.status} />
            {h.overdue && <OverdueBadge />}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            From: {h.shift_name} · {fmt(h.date_time)} · by {h.created_by_name}
            {h.division && <> · {h.division}</>}
            {sendToLabel(h) && <> · <span className="font-semibold text-slate-700" data-testid="handoff-send-to-label">Sent to: {sendToLabel(h)}</span></>}
          </p>
          {(h.completed_by || h.archived_by) && (
            <div className="mt-2 space-y-0.5 text-xs text-slate-500" data-testid="handoff-lifecycle-meta">
              {h.completed_by && <p>Completed by <span className="font-semibold text-slate-700">{h.completed_by}</span> on {fmt(h.completed_at)}</p>}
              {h.archived_by && <p>Archived by <span className="font-semibold text-slate-700">{h.archived_by}</span> on {fmt(h.archived_at)}</p>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {h.acknowledged ? (
            <div data-testid="acknowledged-badge" className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> Acknowledged
            </div>
          ) : (
            <Button data-testid="acknowledge-button" onClick={acknowledge} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
              <UserCheck className="h-4 w-4" /> Acknowledge
            </Button>
          )}

          {h.status !== "completed" && h.status !== "archived"
            && (h.created_by === user.id || isAdmin || user.role === "supervisor") && (
            <Button
              data-testid="edit-handoff-button"
              variant="outline"
              className="gap-2 text-blue-700 border-blue-200 hover:bg-blue-50"
              onClick={() => navigate(`/handoffs/${h.id}/edit`)}
            >
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          )}

          {h.status !== "completed" && h.status !== "archived" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="complete-handoff-button" variant="outline" className="gap-2 text-violet-700 border-violet-200 hover:bg-violet-50">
                  <CheckSquare className="h-4 w-4" /> Complete Handoff
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Complete this handoff?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Please confirm that all relevant tasks have been completed or carried forward to the next shift. This marks the handoff as Completed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="complete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="complete-confirm" onClick={completeHandoff} className="bg-violet-600 hover:bg-violet-700 text-white">
                    Yes, complete handoff
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {h.status !== "archived" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="archive-handoff-button" variant="outline" className="gap-2 text-slate-600 hover:bg-slate-100">
                  <Archive className="h-4 w-4" /> Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive this handoff?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Archived handoffs are removed from the active dashboard but remain available in the Archive page. Acknowledgement and task history are preserved.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="archive-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="archive-confirm" onClick={archiveHandoff} className="bg-slate-800 hover:bg-slate-900 text-white">
                    Archive handoff
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="space-y-6 rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <Section icon={ClipboardList} title="Shift Summary">
              <p>{h.summary}</p>
            </Section>
            {h.incidents && (
              <Section icon={AlertTriangle} title="Notable Incidents" tint="text-amber-600">
                <p>{h.incidents}</p>
              </Section>
            )}
            {h.equipment_issues && (
              <Section icon={Wrench} title="Equipment Issues" tint="text-orange-600">
                <p className="rounded-md border border-orange-100 bg-orange-50/50 p-3">{h.equipment_issues}</p>
              </Section>
            )}
          </Card>

          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <ClipboardList className="h-4 w-4 text-indigo-600" /> Tasks
            </h3>
            <div className="mt-3 space-y-2" data-testid="handoff-tasks">
              {h.tasks.length === 0 ? (
                <p className="text-sm text-slate-400">No tasks on this handoff.</p>
              ) : h.tasks.map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-800">{t.title}</span>
                    <TaskStatusBadge status={t.status} />
                  </div>
                  {t.status === "open" && (
                    <div className="flex gap-2">
                      <Button data-testid={`complete-task-${t.id}`} onClick={() => taskAction(t.id, "complete")} size="sm" variant="outline" className="h-8 gap-1 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        <Check className="h-3.5 w-3.5" /> Complete
                      </Button>
                      <Button data-testid={`carry-task-${t.id}`} onClick={() => taskAction(t.id, "carry-forward")} size="sm" variant="outline" className="h-8 gap-1 text-amber-700 border-amber-200 hover:bg-amber-50">
                        <ArrowRightCircle className="h-3.5 w-3.5" /> Carry forward
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <MessageSquare className="h-4 w-4 text-blue-600" /> Comments
            </h3>
            <div className="mt-3 space-y-3" data-testid="comments-list">
              {h.comments?.length === 0 && <p className="text-sm text-slate-400">No comments yet.</p>}
              {h.comments?.map((c) => (
                <div key={c.id} className="rounded-md border border-slate-100 bg-slate-50 p-3">
                  <p className="text-sm text-slate-700">{c.text}</p>
                  <p className="mt-1 text-xs text-slate-400">{c.user_name} · {fmt(c.created_at)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Add a comment…" />
              <Button data-testid="comment-submit" onClick={addComment} className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white self-end">Post</Button>
            </div>
          </Card>
        </div>

        <div>
          <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
              <UserCheck className="h-4 w-4 text-emerald-600" /> Acknowledgements
            </h3>
            <div className="mt-3 space-y-2" data-testid="acknowledgements-list">
              {h.acknowledgements.length === 0 ? (
                <p className="text-sm text-slate-400">No one has acknowledged yet.</p>
              ) : h.acknowledgements.map((a) => (
                <div key={a.id} className="flex items-center gap-2 rounded-md border border-slate-100 p-2.5">
                  <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-100 text-[11px] font-bold text-emerald-700">
                    {a.user_name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{a.user_name}</p>
                    <p className="text-xs text-slate-400">{fmt(a.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {h.intended_recipients && h.intended_recipients.length > 0 && (
            <Card className="mt-6 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="intended-recipients">
              <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
                <UserCheck className="h-4 w-4 text-blue-600" /> Intended Recipients
              </h3>
              {h.incoming_supervisor && (
                <p className="mt-1 text-xs text-slate-500">Incoming supervisor: <span className="font-semibold text-slate-700">{h.incoming_supervisor.name}</span></p>
              )}
              <div className="mt-3 space-y-2">
                {h.intended_recipients.map((r) => (
                  <div key={r.id} data-testid={`recipient-${r.id}`} className="flex items-center justify-between rounded-md border border-slate-100 p-2.5">
                    <p className="text-sm font-medium text-slate-800">{r.name}</p>
                    {r.acknowledged
                      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Acknowledged</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Pending</span>}
                  </div>
                ))}
              </div>
            </Card>
          )}

        </div>
      </div>
    </div>
  );
}
